import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, List, ListItemButton, ListItemText, Paper, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AppShell from '../components/AppShell';
import api from '../services/api';

/**
 * Where partners land after signing in. At the top, the Quota Monitor Lookup they're used to:
 * type a view's number to open it (the view page refuses views not shared with them, as
 * before). Underneath, the quota views shared with their account (7k).
 */
const PartnerViewsPage = () => {
  const navigate = useNavigate();
  const [quotaMonitorId, setQuotaMonitorId] = useState('');
  const [views, setViews] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/quota-monitors/mine/').then(({ data }) => setViews(data)).catch(() => setError("Your quota views can't be loaded just now."));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (quotaMonitorId) {
      navigate(`/monitor-quotas/${quotaMonitorId}`);
    } else {
      alert('Please enter a valid Quota Monitor ID');
    }
  };

  return (
    <AppShell maxWidth="sm">
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <SearchIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700} gutterBottom>
          Quota Monitor Lookup
        </Typography>
        <Typography color="text.secondary">Enter a Quota Monitor ID to view its quota progress.</Typography>
      </Box>

      <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 3, p: 4, mb: 5 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Quota Monitor ID"
            value={quotaMonitorId}
            onChange={(e) => setQuotaMonitorId(e.target.value)}
            placeholder="Enter ID"
            required
            fullWidth
            autoFocus
          />
          <Button variant="contained" type="submit" sx={{ borderRadius: 2, textTransform: 'none', py: 1.2 }}>
            Look up
          </Button>
        </Box>
      </Box>

      <Typography variant="h5" component="h2" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ mb: 0.5 }}>
        Your quota views
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>The views shared with your account. Open one to see its quotas&apos; progress.</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!views && !error && <CircularProgress size={24} />}
      {views && views.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">No quota views have been shared with your account yet. Ask your Stack contact.</Typography>
        </Paper>
      )}
      {views && views.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
          <List disablePadding>
            {views.map((v, i) => (
              <ListItemButton key={v.id} component={Link} to={`/monitor-quotas/${v.id}`} divider={i < views.length - 1}>
                <ListItemText primary={v.name} primaryTypographyProps={{ fontWeight: 500 }} />
                <ChevronRightRoundedIcon color="action" />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </AppShell>
  );
};

export default PartnerViewsPage;
